"use client"

import { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react"
import { useTranslations } from "next-intl"
import { cn } from "@/shared/lib/utils"
import { subscribeToMediaQuery } from "@/shared/lib/media-query"
import { useGenerationStore } from "../store"
import { GENERATION_STYLES, ASPECT_RATIOS, IMAGE_COUNTS } from "../constants"
import { useAuthStore } from "@/features/auth/store"
import { FREE_LIMITS, isPaidTier } from "@/features/credits/constants"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuCheckboxItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/shared/components/ui/dropdown-menu"
import { Icon } from "@/shared/components/icons"

/** Prompt dock is z-[100]; portaled pickers must paint above it. */
const CONTROL_PICKER_Z = "z-[110]"

/** Below `sm` (640px): Radix submenus only open left/right and often overflow; use inline panels instead. */
const COMPACT_SETTINGS_MENU_MQ = "(max-width: 639px)"

const CONTROL_MENU_PLACEMENT = {
  side: "top" as const,
  sideOffset: 10,
  align: "end" as const,
}

const GLASS_MENU =
  "rounded-2xl border border-white/[0.1] bg-[rgba(18,18,20,0.92)] backdrop-blur-xl backdrop-saturate-150 shadow-[0_16px_48px_rgba(0,0,0,0.5)] p-1.5"

/** Radix caps menu height to available viewport space — adds inner scroll above the bottom dock; use natural height instead. */
const SETTINGS_MENU_NO_INNER_SCROLL =
  "[max-height:unset!important] overflow-x-visible overflow-y-visible overscroll-none"

/** Prevent wheel from scrolling the page while the cursor is over a flyout menu. */
function stopSettingsMenuWheel(e: React.WheelEvent) {
  e.preventDefault()
  e.stopPropagation()
}

/** Shared hover-open + delayed close for the style pill only. */
function useHoverOpenState(options?: {
  closeDelayMs?: number
  onScheduledClose?: () => void
}) {
  const closeDelayMs = options?.closeDelayMs ?? 120
  const onScheduledClose = options?.onScheduledClose
  const [open, setOpen] = useState(false)
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelClose = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current)
      closeTimeout.current = null
    }
  }, [])

  const scheduleClose = useCallback(() => {
    cancelClose()
    closeTimeout.current = setTimeout(() => {
      setOpen(false)
      onScheduledClose?.()
    }, closeDelayMs)
  }, [cancelClose, closeDelayMs, onScheduledClose])

  useEffect(() => {
    return () => {
      if (closeTimeout.current) clearTimeout(closeTimeout.current)
    }
  }, [])

  const openNow = useCallback(() => {
    cancelClose()
    setOpen(true)
  }, [cancelClose])

  const holdOpen = useCallback(() => {
    cancelClose()
  }, [cancelClose])

  const suppressFocusSteal = {
    onOpenAutoFocus: (e: Event) => e.preventDefault(),
    onCloseAutoFocus: (e: Event) => e.preventDefault(),
  }

  return {
    open,
    setOpen,
    triggerHoverHandlers: {
      onMouseEnter: openNow,
      onMouseLeave: scheduleClose,
    },
    contentHoverHandlers: {
      onMouseEnter: holdOpen,
      onMouseLeave: scheduleClose,
    },
    suppressFocusSteal,
  }
}

function PillButton({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-white/[0.1] bg-white/[0.08] px-3 py-1.5",
        "text-xs font-semibold text-white",
        "transition-all hover:border-white/[0.14] hover:bg-white/[0.12]",
        "[&_svg]:text-zinc-300",
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
}

/** Compute display dimensions for aspect ratio preview boxes. */
function getRatioBox(value: string, maxSize: number) {
  const parts = value.split(":").map(Number)
  const w = parts[0]
  const h = parts[1]
  const ratio = w / h
  if (ratio >= 1) {
    return { width: maxSize, height: Math.round(maxSize / ratio) }
  }
  return { width: Math.round(maxSize * ratio), height: maxSize }
}

const LANDSCAPE_RATIOS = ASPECT_RATIOS.slice(0, 4)
const PORTRAIT_RATIOS = ASPECT_RATIOS.slice(4)

function AspectRatioPickerGrid({
  value,
  onChange,
  showPreview = true,
}: {
  value: string
  onChange: (v: string) => void
  /** Live frame preview; disabled in compact (mobile) menus to save space—ratio chips are enough. */
  showPreview?: boolean
}) {
  const [hoveredRatio, setHoveredRatio] = useState<string | null>(null)
  const displayRatio = hoveredRatio ?? value
  const previewBox = getRatioBox(displayRatio, 72)

  return (
    <div className={cn("p-1", showPreview && "flex gap-3")}>
      <div className="flex flex-col gap-2">
        <div className="flex gap-1.5">
          {LANDSCAPE_RATIOS.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => onChange(r.value)}
              onMouseEnter={() => setHoveredRatio(r.value)}
              onMouseLeave={() => setHoveredRatio(null)}
              className={cn(
                "px-2.5 py-1.5 rounded-md text-xs font-medium border transition-all",
                r.value === value
                  ? "border-accent-blue bg-accent-blue/10 text-foreground"
                  : "border-border/30 bg-muted/20 text-muted-foreground hover:border-border/50 hover:text-foreground"
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5">
          {PORTRAIT_RATIOS.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => onChange(r.value)}
              onMouseEnter={() => setHoveredRatio(r.value)}
              onMouseLeave={() => setHoveredRatio(null)}
              className={cn(
                "px-2.5 py-1.5 rounded-md text-xs font-medium border transition-all",
                r.value === value
                  ? "border-accent-blue bg-accent-blue/10 text-foreground"
                  : "border-border/30 bg-muted/20 text-muted-foreground hover:border-border/50 hover:text-foreground"
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {showPreview ? (
        <div className="flex h-[100px] w-[100px] shrink-0 items-center justify-center">
          <div
            className="relative rounded-md border-2 border-accent-blue/60 bg-accent-blue/5 transition-all duration-300 ease-out"
            style={{ width: previewBox.width, height: previewBox.height }}
          >
            <div className="absolute -top-px -left-px h-2 w-2 rounded-tl-sm border-t-2 border-l-2 border-accent-blue" />
            <div className="absolute -top-px -right-px h-2 w-2 rounded-tr-sm border-t-2 border-r-2 border-accent-blue" />
            <div className="absolute -bottom-px -left-px h-2 w-2 rounded-bl-sm border-b-2 border-l-2 border-accent-blue" />
            <div className="absolute -bottom-px -right-px h-2 w-2 rounded-br-sm border-b-2 border-r-2 border-accent-blue" />
            <div className="absolute inset-0 flex items-center">
              <div className="w-full h-px bg-accent-blue/15" />
            </div>
            <div className="absolute inset-0 flex justify-center">
              <div className="h-full w-px bg-accent-blue/15" />
            </div>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-accent-blue/70">
              {displayRatio}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function GenerationNumberRadioList({
  numImages,
  setNumImages,
  userIsPaid,
  maxFreeImages,
}: {
  numImages: number
  setNumImages: (n: number) => void
  userIsPaid: boolean
  maxFreeImages: number
}) {
  const t = useTranslations("generate")

  return (
    <DropdownMenuRadioGroup
      value={String(numImages)}
      onValueChange={(v) => {
        const count = Number(v)
        if (!userIsPaid && count > maxFreeImages) return
        setNumImages(count)
      }}
    >
      {IMAGE_COUNTS.map((count) => {
        const isLocked = !userIsPaid && count > maxFreeImages
        return (
          <DropdownMenuRadioItem
            key={count}
            value={String(count)}
            disabled={isLocked}
            className={cn("rounded-lg", isLocked && "opacity-50")}
          >
            <span className="flex w-full items-center gap-2">
              {t("settingsMenu.imageCount", { count })}
              {isLocked ? <Icon name="lock" size={12} className="ml-auto size-3 text-muted-foreground" /> : null}
            </span>
          </DropdownMenuRadioItem>
        )
      })}
    </DropdownMenuRadioGroup>
  )
}

/** Circular glass control (settings) — matches dock send button size. */
export function GenerationSettingsMenu({
  className,
  title,
}: {
  className?: string
  /** Native tooltip (icon-only control). */
  title?: string
}) {
  const aspectRatio = useGenerationStore((s) => s.aspectRatio)
  const setAspectRatio = useGenerationStore((s) => s.setAspectRatio)
  const numImages = useGenerationStore((s) => s.numImages)
  const setNumImages = useGenerationStore((s) => s.setNumImages)
  const publishToExplore = useGenerationStore((s) => s.publishToExplore)
  const setPublishToExplore = useGenerationStore((s) => s.setPublishToExplore)
  const showAdvanced = useGenerationStore((s) => s.showAdvanced)
  const toggleAdvanced = useGenerationStore((s) => s.toggleAdvanced)
  const t = useTranslations("generate")
  const settingsTitle = title ?? t("setting")

  const user = useAuthStore((s) => s.user)
  const userIsPaid = user ? isPaidTier(user.subscription) : false
  const maxFreeImages = FREE_LIMITS.maxImagesPerGeneration

  const [mobileSheet, setMobileSheet] = useState<null | "size" | "number">(null)
  const [isCompactMenu, setIsCompactMenu] = useState(false)

  useLayoutEffect(() => {
    const mq = window.matchMedia(COMPACT_SETTINGS_MENU_MQ)
    const sync = () => setIsCompactMenu(mq.matches)
    sync()
    return subscribeToMediaQuery(mq, sync)
  }, [])

  const toggleMobileSheet = (key: "size" | "number") => {
    setMobileSheet((prev) => (prev === key ? null : key))
  }

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) setMobileSheet(null)
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title={settingsTitle}
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-full border text-white transition-all",
            showAdvanced
              ? "border-[#5aa7ff]/45 bg-[#1d7df2]/22 shadow-[0_0_22px_rgba(29,125,242,0.28)] hover:bg-[#1d7df2]/28"
              : "border-white/[0.1] bg-white/[0.08] hover:bg-white/[0.12]",
            "active:scale-[0.97]",
            className
          )}
          aria-label={settingsTitle}
        >
          <Icon name="slider" size={17} className="size-[17px]" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        {...CONTROL_MENU_PLACEMENT}
        collisionPadding={16}
        className={cn(
          GLASS_MENU,
          isCompactMenu
            ? "min-w-0 w-[min(100vw-2rem,18rem)] max-w-[calc(100vw-2rem)]"
            : "min-w-[220px]",
          CONTROL_PICKER_Z,
          SETTINGS_MENU_NO_INNER_SCROLL
        )}
        onWheel={stopSettingsMenuWheel}
      >
        {isCompactMenu ? (
          <>
            <DropdownMenuItem
              className={cn(
                "gap-2 rounded-xl",
                mobileSheet === "size" && "bg-white/[0.06]"
              )}
              onSelect={(e) => {
                e.preventDefault()
                toggleMobileSheet("size")
              }}
            >
              <Icon name="row" size={14} className="size-3.5 shrink-0 text-zinc-400" />
              <span className="min-w-0 flex-1 text-left">{t("settingsMenu.size")}</span>
              <span className="shrink-0 text-xs font-normal tabular-nums text-zinc-500">
                {aspectRatio}
              </span>
              <Icon
                name="arrow-down-2"
                size={16}
                className={cn(
                  "ml-0.5 size-4 shrink-0 text-zinc-500 transition-transform",
                  mobileSheet === "size" && "rotate-180"
                )}
                aria-hidden
              />
            </DropdownMenuItem>
            {mobileSheet === "size" ? (
              <div
                className="max-w-full overflow-x-auto border-t border-white/[0.06] px-1 py-2"
                onPointerDown={(e) => e.stopPropagation()}
              >
                <AspectRatioPickerGrid
                  value={aspectRatio}
                  onChange={setAspectRatio}
                  showPreview={false}
                />
              </div>
            ) : null}

            <DropdownMenuItem
              className={cn(
                "gap-2 rounded-xl",
                mobileSheet === "number" && "bg-white/[0.06]"
              )}
              onSelect={(e) => {
                e.preventDefault()
                toggleMobileSheet("number")
              }}
            >
              <Icon name="category" size={14} className="size-3.5 shrink-0 text-zinc-400" />
              <span className="min-w-0 flex-1 text-left">{t("settingsMenu.number")}</span>
              <span className="shrink-0 text-xs font-normal text-zinc-500">{numImages}x</span>
              <Icon
                name="arrow-down-2"
                size={16}
                className={cn(
                  "ml-0.5 size-4 shrink-0 text-zinc-500 transition-transform",
                  mobileSheet === "number" && "rotate-180"
                )}
                aria-hidden
              />
            </DropdownMenuItem>
            {mobileSheet === "number" ? (
              <div
                className="border-t border-white/[0.06] px-1 py-1"
                onPointerDown={(e) => e.stopPropagation()}
              >
                <GenerationNumberRadioList
                  numImages={numImages}
                  setNumImages={setNumImages}
                  userIsPaid={userIsPaid}
                  maxFreeImages={maxFreeImages}
                />
              </div>
            ) : null}

            <DropdownMenuItem
              className={cn(
                "gap-2 rounded-xl",
                showAdvanced && "bg-white/[0.06] text-white"
              )}
              onSelect={() => {
                toggleAdvanced()
              }}
            >
              <Icon name="setting" size={14} className="size-3.5 shrink-0 text-zinc-400" />
              <span className="min-w-0 flex-1 text-left">{t("settingsMenu.advanced")}</span>
              <span className="shrink-0 text-xs font-normal text-zinc-500">
                {showAdvanced ? t("settingsMenu.on") : t("settingsMenu.off")}
              </span>
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger
                className={cn(
                  "w-full min-w-0 gap-2 rounded-xl data-[state=open]:bg-white/[0.06]"
                )}
              >
                <Icon name="row" size={14} className="size-3.5 shrink-0 text-zinc-400" />
                <span className="min-w-0 flex-1 text-left">{t("settingsMenu.size")}</span>
                <span className="shrink-0 text-xs font-normal tabular-nums text-zinc-500">
                  {aspectRatio}
                </span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent
                sideOffset={6}
                alignOffset={-2}
                collisionPadding={20}
                className={cn(
                  GLASS_MENU,
                  "z-[120] min-w-[min(92vw,320px)]",
                  SETTINGS_MENU_NO_INNER_SCROLL
                )}
                onWheel={stopSettingsMenuWheel}
              >
                <AspectRatioPickerGrid value={aspectRatio} onChange={setAspectRatio} />
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger
                className={cn(
                  "w-full min-w-0 gap-2 rounded-xl data-[state=open]:bg-white/[0.06]"
                )}
              >
                <Icon name="category" size={14} className="size-3.5 shrink-0 text-zinc-400" />
                <span className="min-w-0 flex-1 text-left">{t("settingsMenu.number")}</span>
                <span className="shrink-0 text-xs font-normal text-zinc-500">{numImages}x</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent
                sideOffset={6}
                alignOffset={-2}
                collisionPadding={20}
                className={cn(
                  GLASS_MENU,
                  "z-[120] min-w-[10rem]",
                  SETTINGS_MENU_NO_INNER_SCROLL
                )}
                onWheel={stopSettingsMenuWheel}
              >
                <GenerationNumberRadioList
                  numImages={numImages}
                  setNumImages={setNumImages}
                  userIsPaid={userIsPaid}
                  maxFreeImages={maxFreeImages}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuItem
              className={cn(
                "gap-2 rounded-xl",
                showAdvanced && "bg-white/[0.06] text-white"
              )}
              onSelect={() => {
                toggleAdvanced()
              }}
            >
              <Icon name="setting" size={14} className="size-3.5 shrink-0 text-zinc-400" />
              <span className="min-w-0 flex-1 text-left">{t("settingsMenu.advanced")}</span>
              <span className="shrink-0 text-xs font-normal text-zinc-500">
                {showAdvanced ? t("settingsMenu.on") : t("settingsMenu.off")}
              </span>
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuCheckboxItem
          className="rounded-xl"
          checked={publishToExplore}
          onCheckedChange={(v) => setPublishToExplore(v === true)}
        >
          <Icon name="monitor" size={14} className="size-3.5 text-zinc-400" />
          {t("settingsMenu.publishToExplore")}
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function ControlPills() {
  const t = useTranslations("generate")
  const style = useGenerationStore((s) => s.style)
  const setStyle = useGenerationStore((s) => s.setStyle)
  const currentStyle = GENERATION_STYLES.find((s) => s.value === style)
  const styleMenuHover = useHoverOpenState()

  return (
    <div className="flex flex-wrap items-center gap-2">
      <DropdownMenu open={styleMenuHover.open} onOpenChange={styleMenuHover.setOpen} modal={false}>
        <DropdownMenuTrigger asChild>
          <PillButton {...styleMenuHover.triggerHoverHandlers}>
            <Icon name="edit" size={14} className="size-3.5" />
            {currentStyle ? t(`styles.${currentStyle.value}.label`) : t("style")}
          </PillButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="top"
          sideOffset={8}
          align="start"
          className={cn(GLASS_MENU, "w-48", CONTROL_PICKER_Z)}
          {...styleMenuHover.contentHoverHandlers}
          {...styleMenuHover.suppressFocusSteal}
        >
          <DropdownMenuRadioGroup value={style} onValueChange={setStyle}>
            {GENERATION_STYLES.map((option) => (
              <DropdownMenuRadioItem key={option.value} value={option.value} className="rounded-lg">
                <span className="flex items-center gap-2">
                  <Icon name={option.iconName} size={14} className="size-3.5 text-muted-foreground" />
                  {t(`styles.${option.value}.label`)}
                </span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
